import { useLingui } from '@lingui/react/macro';
import { Headphone, MessageEdit } from 'iconsax-reactjs';
import { type FC, useEffect, useState } from 'react';
import { useAsync } from 'react-use';
import { initiateChatwoot, openChatwoot } from '@/base/chatwoot';
import { WORKSPACE } from '@/base/constants';
import { initiateUserback, openUserback } from '@/base/userback';
import { Button } from '@/shared/Button';
import { ClientOnly } from '@/shared/ClientOnly';
import { PopoverContent, PopoverRoot, PopoverTrigger } from '@/shared/Popover';
import { cn, useIsDesktop } from '@/shared/react';

const OPEN_EVENTS = ['chatwoot:opened', 'userback:opened'];
const CLOSE_EVENTS = ['chatwoot:closed', 'userback:closed'];

/** Both widgets park themselves in this corner once opened. */
const useIsWidgetOpen = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const open = () => setIsOpen(true);
    const close = () => setIsOpen(false);
    for (const name of OPEN_EVENTS) window.addEventListener(name, open);
    for (const name of CLOSE_EVENTS) window.addEventListener(name, close);
    return () => {
      for (const name of OPEN_EVENTS) window.removeEventListener(name, open);
      for (const name of CLOSE_EVENTS) window.removeEventListener(name, close);
    };
  }, []);

  return isOpen;
};

const RawSupportButton: FC = () => {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const isWidgetOpen = useIsWidgetOpen();
  const hasBottomNav =
    (WORKSPACE === 'app' || WORKSPACE === 'admin') && !isDesktop;

  // Both widgets memoize their own boot promise, so re-running them here costs
  // nothing and resolves to null when they are unconfigured or failed to load.
  const chatwoot = useAsync(initiateChatwoot);
  const userback = useAsync(initiateUserback);

  if (!chatwoot.value && !userback.value) return null;

  return (
    <div
      className={cn(
        'fixed end-4 z-20 transition-all duration-200',
        hasBottomNav ? 'bottom-20' : 'bottom-4',
        isWidgetOpen && 'opacity-0 translate-y-2 pointer-events-none',
      )}
    >
      <PopoverRoot open={open} onOpenChange={setOpen}>
        <PopoverTrigger>
          <Button
            size={10}
            type="div"
            variant="primary"
            className="!rounded-full shadow-[0_8px_24px_rgba(12,12,12,0.24)]"
            aria-label={t`Support`}
          >
            <Headphone size={18} className="shrink-0" />
            {t`Support`}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="w-max p-1">
          <div className="flex flex-col gap-1">
            {chatwoot.value && (
              <Button
                size={10}
                block
                variant="transparent"
                className="justify-start gap-3"
                onClick={() => {
                  setOpen(false);
                  void openChatwoot();
                }}
              >
                <Headphone size={18} className="shrink-0" />
                {t`Talk to Support`}
              </Button>
            )}
            {userback.value && (
              <Button
                size={10}
                block
                variant="transparent"
                className="justify-start gap-3"
                onClick={() => {
                  setOpen(false);
                  void openUserback();
                }}
              >
                <MessageEdit size={18} className="shrink-0" />
                {t`Send Feedback`}
              </Button>
            )}
          </div>
        </PopoverContent>
      </PopoverRoot>
    </div>
  );
};

export const SupportButton: FC = () => (
  <ClientOnly>
    <RawSupportButton />
  </ClientOnly>
);
